/**
 * Axios middleware script file
 *
 * @package    Middleware
 * @author     Vadym Karpenko <vadym.karpenko@door3.com>
 */

import axios from 'axios';
import {
    push,
    replace,
} from 'connected-react-router';
import { SubmissionError } from 'redux-form';
import Qs from 'qs';

import {
    addNotify,
} from 'Actions/notifications';
import unflatten from 'Utils/unflatten';

/**
 * Axios middleware
 *
 * @param interceptors
 *
 * @returns {function(*=)}
 */
export default (interceptors) => (store) => {
    const axiosInstance = () => {
        const state = store.getState();

        const headers = {
            Accept: 'application/json',
            'Accept-Language': state.translations.locale,
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
        };

        const instance = axios.create({
            baseURL: state.general.apiHost,
            headers,
            paramsSerializer: (params) => {
                if (params.criteria) {
                    let { include, page, perPage, sort = [], where = [] } = params.criteria;

                    params.criteria = {
                        include,
                        page: page ? page + 1 : undefined,
                        perPage,
                        sort: Array.isArray(sort) ? sort.reduce((sort, { desc, id }) => {
                            sort[id] = desc ? 'DESC' : 'ASC';

                            return sort;
                        }, {}) : sort,
                        where: Array.isArray(where) ? where.reduce((where, { id, value }) => {
                            where[id] = `%${ value }%`;

                            return where;
                        }, {}) : where,
                    };
                }

                return Qs.stringify(params, {
                    arrayFormat: 'comma',
                    encodeValuesOnly: true,
                });
            },
            responseType: 'json',
            transformResponse: [
                (data) => {
                    // In IE11 request.response always returns string although responseType is given "json"
                    if (typeof data === 'string') {
                        try {
                            // eslint-disable-next-line
                            data = JSON.parse(data);
                            // eslint-disable-next-line
                        } catch (error) {}
                    }
                    if (data.code === 404) {
                        store.dispatch(replace('/404'));
                    }
                    if (data.code === 401 || data.code === 403) {
                        store.dispatch(replace('/'));
                    }
                    if ([
                        400,
                        500,
                    ].includes(data.code)) {
                        store.dispatch(push('/500'));
                    }

                    return data;
                },
            ],
        });

        if (interceptors) {
            interceptors.map((interceptor) => interceptor(instance));
        }

        return instance;
    };

    return (next) => (action) => {
        if (action.request && !action.type) {
            return apiCall(action)(next, axiosInstance(), store.dispatch, store.getState);
        }

        const result = next(action);

        if (typeof result === 'function') {
            return result(axiosInstance());
        }

        return result;
    };
};

/**
 * Default callback for process response
 *
 * @param {Object} response
 * @param {Object?} response.data
 * @param {Object?} response.metadata
 * @param {String?} response.message
 * @param {Object?} response.details
 *
 * @returns {{payload: {} | *}}
 */
const defaultCallback = (response) => {
    if (!response) {
        return {};
    }

    const { data, details, message, metadata } = response;
    let result = {
        data,
        details,
        message,
        metadata,
    };

    Object.keys(result).forEach((key) => result[key] === undefined ? delete result[key] : '');

    return result;
};

/**
 /**
 * Create API call action
 *
 * @param {String} requestType
 * @param {function} request
 * @param {function} successCallback function
 * @param {function} errorCallback function
 *
 * @return {function}
 */
const apiCall = ({ requestType, request, successCallback = defaultCallback, errorCallback = defaultCallback }) => (next, axiosInstance, dispatch, getState) => {
    next({
        type: `${ requestType }_REQUEST`,
    });

    return request(axiosInstance).then((response) => {
        try {
            const payload = successCallback(response.data, dispatch, getState);

            if (payload.message) {
                dispatch(addNotify(payload.message));
            }
            if (payload) {
                return next({
                    type: `${ requestType }_SUCCESS`,
                    ...payload,
                });
            }
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.log(error); // eslint-disable-line
            }

            return next({
                payload: {},
                type: `${ requestType }_ERROR`,
            });
        }
    }).catch((error) => {
        if (!error.response) {
            return next({
                type: `${ requestType }_ERROR`,
            });
        }

        const payload = errorCallback(error.response.data, dispatch, getState);

        if (payload.message) {
            dispatch(addNotify(payload.message, 'error'));
        }
        if (payload) {
            next({
                type: `${ requestType }_ERROR`,
                ...payload,
            });
            if (error.response.status === 422 && payload.details) {
                throw new SubmissionError(unflatten(payload.details));
            }
        }
    });
};
